import { useCallback, useMemo } from 'react';

import { SEARCH_PRODUCT_LIMIT } from '@/common/common.contants';
import i18n from '@/configs/i18n/i18n';
import { SearchDataProcessor } from '@/lib/search/SearchDataProcessor';
import {
  SearchFilters,
  SearchProductPayload,
  SearchServiceResult,
} from '@/types';
import { DefaultLocale, IAvailableLocaleTypes } from '@/types/locale.types';
import { isReactNativeWebView } from '@/utils/common';
import { COUNTRY_CODE } from '@/utils/constants';

export const useSearchResult = () => {
  const locale = (i18n.language as IAvailableLocaleTypes) ?? DefaultLocale;

  const searchDataProcessor = useMemo(
    () => new SearchDataProcessor(locale),
    [locale]
  );

  const requestPayload = useCallback(
    (
      query: string,
      pageNo: number,
      size?: number,
      filters?: SearchFilters,
      autocorrect: boolean = true,
      sortBy: string = 'RELEVANCE',
      autocategorise: boolean = true,
      storeId?: number | undefined,
      barcodeQuery?: string
    ) => {
      const Filters = {
        ...filters,
        countryCode: COUNTRY_CODE,
        availability: true,
        ...(storeId ? { storeCodes: [storeId.toString()] } : {}),
        ...(barcodeQuery ? { barcode: barcodeQuery } : {}),
      };

      const payload: SearchProductPayload = {
        q: barcodeQuery ? '*' : query.trim().toLocaleLowerCase(),
        page: pageNo,
        size: size || SEARCH_PRODUCT_LIMIT,
        filters: Filters,
        autocategorise,
        autocorrect,
        source: isReactNativeWebView() ? 'app' : 'web',
        includeCurations: true,
        sortBy: sortBy,
      };
      return payload;
    },
    []
  );

  const processSearchResult = useCallback(
    async (searchResult: SearchServiceResult) => {
      const { initialProducts, totalProducts } =
        searchDataProcessor.processInitialSearchResults(searchResult);

      return {
        products: initialProducts ?? [],
        totalProducts,
      };
    },
    [searchDataProcessor]
  );

  return {
    requestPayload,
    processSearchResult,
  };
};
